// MUI Component overrides for consistent styling

export const components = {
  MuiCssBaseline: {
    styleOverrides: {
      '*': {
        boxSizing: 'border-box',
        margin: 0,
        padding: 0,
      },
      html: {
        scrollBehavior: 'smooth',
      },
      body: {
        overflowX: 'hidden',
      },
      'a': {
        textDecoration: 'none',
        color: 'inherit',
      },
      '::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '::-webkit-scrollbar-track': {
        background: '#f1f1f1',
      },
      '::-webkit-scrollbar-thumb': {
        background: '#c1c1c1',
        borderRadius: '4px',
      },
      '::-webkit-scrollbar-thumb:hover': {
        background: '#a1a1a1',
      },
    },
  },

  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        padding: '10px 24px',
        fontWeight: 600,
        fontSize: '0.9375rem',
        textTransform: 'none',
        boxShadow: 'none',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 'none',
          transform: 'translateY(-2px)',
        },
      },
      containedPrimary: {
        background: 'linear-gradient(135deg, #00D4FF 0%, #5B8DEF 100%)',
        '&:hover': {
          background: 'linear-gradient(135deg, #00E5FF 0%, #8BB4F8 100%)',
        },
      },
      containedSecondary: {
        background: 'linear-gradient(135deg, #5B8DEF 0%, #3D6FD9 100%)',
        '&:hover': {
          background: 'linear-gradient(135deg, #8BB4F8 0%, #5B8DEF 100%)',
        },
      },
      outlined: {
        borderWidth: '2px',
        '&:hover': {
          borderWidth: '2px',
        },
      },
      outlinedPrimary: {
        borderColor: '#00B8D4',
        color: '#00B8D4',
        '&:hover': {
          borderColor: '#00D4FF',
          backgroundColor: 'rgba(0, 212, 255, 0.08)',
        },
      },
      sizeLarge: {
        padding: '14px 32px',
        fontSize: '1rem',
      },
      sizeSmall: {
        padding: '6px 16px',
        fontSize: '0.8125rem',
      },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12)',
          transform: 'translateY(-4px)',
        },
      },
    },
  },

  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '24px',
        '&:last-child': {
          paddingBottom: '24px',
        },
      },
    },
  },

  MuiContainer: {
    styleOverrides: {
      root: {
        paddingLeft: '16px',
        paddingRight: '16px',
        '@media (min-width:600px)': {
          paddingLeft: '24px',
          paddingRight: '24px',
        },
        '@media (min-width:900px)': {
          paddingLeft: '32px',
          paddingRight: '32px',
        },
      },
      maxWidthLg: {
        '@media (min-width:1280px)': {
          maxWidth: '1280px',
        },
      },
      maxWidthXl: {
        '@media (min-width:1536px)': {
          maxWidth: '1440px',
        },
      },
    },
  },

  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: 'none',
        backgroundColor: 'transparent',
      },
    },
  },

  MuiToolbar: {
    styleOverrides: {
      root: {
        minHeight: '64px',
        '@media (min-width:600px)': {
          minHeight: '72px',
        },
        '@media (min-width:900px)': {
          minHeight: '80px',
        },
      },
    },
  },

  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRadius: '0 16px 16px 0',
      },
    },
  },

  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#00B8D4',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#00B8D4',
            borderWidth: '2px',
          },
        },
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        fontWeight: 500,
      },
      colorPrimary: {
        background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(91, 141, 239, 0.1) 100%)',
        color: '#00B8D4',
        border: '1px solid rgba(0, 184, 212, 0.3)',
      },
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: 'rgba(0, 0, 0, 0.08)',
      },
    },
  },

  MuiLink: {
    styleOverrides: {
      root: {
        textDecoration: 'none',
        transition: 'color 0.2s ease',
        '&:hover': {
          textDecoration: 'none',
        },
      },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: {
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: 'rgba(0, 184, 212, 0.08)',
        },
      },
    },
  },

  MuiAccordion: {
    styleOverrides: {
      root: {
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          margin: '16px 0',
        },
      },
    },
  },

  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        padding: '0 24px',
        minHeight: '64px',
      },
    },
  },

  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        padding: '0 24px 24px',
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.9375rem',
        minWidth: 'auto',
        padding: '12px 24px',
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      indicator: {
        height: '3px',
        borderRadius: '3px 3px 0 0',
        background: 'linear-gradient(135deg, #00D4FF 0%, #5B8DEF 100%)',
      },
    },
  },
};

export default components;
